import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import ClientKnowledgeBaseLink from "knowledge-base/libraries/_views/client-knowledge-base-link";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import WorkAgreement from "work/work-agreements/work-agreement";
import { workPaneNarrowerThan } from "work/_views/work-pane-layout";

const ListContainer = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  workAgreement: WorkAgreement;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { workAgreement } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const policyDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download the document. Please try again later.", {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (Masked.isMasked(workAgreement.clientPolicies) || Masked.isMasked(workAgreement.vendorPolicies)) {
    return "You do not have permission to view contracts.";
  }

  const policyGroups = [
    {
      documents: workAgreement.clientPolicies,
      documentType: WorkDocumentType.ClientPolicy,
    },
    {
      documents: workAgreement.vendorPolicies,
      documentType: WorkDocumentType.VendorPolicy,
    },
  ];
  const policyDocumentsCount = policyGroups.reduce((count, group) => count + group.documents.length, 0);

  return (
    <ListContainer container direction="row">
      <Grid>
        {policyDocumentsCount === 0 && (
          <HelperText>No policy documents</HelperText>
        )}
        {policyGroups.map((group) => (
          group.documents.length > 0 && (
            <DocumentList
              key={group.documentType}
              documents={group.documents}
              documentType={group.documentType}
              downloadingFileId={isDownloadingFile}
              onDownload={handleDownloadDocumentById}
            />
          )
        ))}
        {/*
          A client's rules are not only the documents attached here — the ones they wrote as
          articles live in their knowledge base, which this agreement is what grants access to.
          Renders nothing on the client's own side of the agreement, and nothing once the
          agreement no longer opens that library.
        */}
        <ClientKnowledgeBaseLink
          ownerLegalEntityId={workAgreement.clientLegalEntityId?.value}
        />
      </Grid>
    </ListContainer>
  );
}
